import { TransitionOverride } from '@mantine/core';
import { createGlobalStyle } from 'styled-components';

// Media query conditions cannot contain calc()/var(). Mantine v7's `rem()` wraps
// every value as `calc(... * var(--mantine-scale))`, which silently invalidates
// each `@media (min-width: ...)` built from these breakpoints (e.g. the dashboard
// 2-column deal grid collapsed to 1 column). Emit a plain rem string instead.
const mqRem = (px: number) => `${px / 16}rem`;

export const Z_INDEX = {
	REACT_TINY_POPOVER: 99999,
	MODAL: 999,
	DRAWER: 999,
	MENU: 999,
	NOTIFICATION: 9999999,
	ONE_SCHEMA_MODAL: 1000,
	GLOBAL_MENU: 1000,
	STICKY_HEADER: 10,
	STICKY_HEADER_MENU: 12,
	STICKY_TABLE_HEADER: 2,
	STICKY_PINNED_COLUMN: 3,
	STICKY_PINNED_HEADER: 11,
	FULLSCREEN_TABLE: 100,
} as const;

export const drawerTransitionProps: TransitionOverride = {
	transition: 'slide-left',
	duration: 200,
	timingFunction: 'ease',
};

// v7 doesn't draw a separator between a grouped `{ group, items }` block and the
// trailing flat items, so the "Frequently used" group in currency/country Selects
// lost its divider. Restore it via a bottom border on the group part.
export const groupedSelectDividerStyles = {
	group: {
		borderBottom: '1px solid var(--mantine-color-gray-4)',
		paddingBottom: '0.25rem',
		marginBottom: '0.25rem',
	},
};

export const GlobalStyling = createGlobalStyle`
    /* https://meyerweb.com/eric/tools/css/reset/ */
    /** Reset CSS */
    html,
    body,
    div,
    span,
    applet,
    object,
    iframe,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    blockquote,
    pre,
    a,
    abbr,
    acronym,
    address,
    big,
    cite,
    code,
    del,
    dfn,
    em,
    img,
    ins,
    kbd,
    q,
    s,
    samp,
    small,
    strike,
    strong,
    sub,
    sup,
    tt,
    var,
    b,
    u,
    i,
    center,
    dl,
    dt,
    dd,
    ol,
    ul,
    li,
    fieldset,
    form,
    label,
    legend,
    table,
    caption,
    tbody,
    tfoot,
    thead,
    tr,
    th,
    td,
    article,
    aside,
    canvas,
    details,
    embed,
    figure,
    figcaption,
    footer,
    header,
    hgroup,
    menu,
    nav,
    output,
    ruby,
    section,
    summary,
    time,
    mark,
    audio,
    video {
        margin: 0;
        padding: 0;
        border: 0;
        font-size: 100%;
        font-style: inherit;
        font-variant: inherit;
        font-family: inherit;
        line-height: inherit;
        vertical-align: baseline;
        font-variant-numeric: tabular-nums;
        -moz-font-feature-settings: "tnum";
        -webkit-font-feature-settings: "tnum";
        font-feature-settings: "tnum";
    }

    /* HTML5 display-role reset for older browsers */
    article,
    aside,
    details,
    figcaption,
    figure,
    footer,
    header,
    hgroup,
    menu,
    nav,
    section {
        display: block;
    }

    body {
        line-height: 1;
        font-size: 0.875rem;
        max-width: 100vw;
        overflow-x: hidden;
    }

    ol,
    ul {
        list-style: none;
    }

    blockquote,
    q {
        quotes: none;
    }

    blockquote:before,
    blockquote:after,
    q:before,
    q:after {
        content: '';
        content: none;
    }

    table {
        border-collapse: collapse;
        border-spacing: 0;
    }

    html,
    form,
    input,
    textarea,
    select,
    span,
    a,
    div,
    button {
        outline: none;
        font-family: 'Inter', sans-serif;
        text-rendering: geometricPrecision !important;
        -webkit-font-smoothing: antialiased !important;
    }

    li::marker {
        font-family: 'Inter', sans-serif;
        font-size: 0.8rem;
    }

    a,
    button {
        cursor: pointer;
    }

    a, a:visited, a:active {
        text-decoration: unset;
        color: unset;
    }

    .clickAnimation {
        cursor: pointer;

        &:active {
            scale: 0.95;
        }
    }

    .support-app-compact-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.4;
        display: block;
    }

    .clickAnimationSubtle {
        cursor: pointer;

        &:active {
            scale: 0.99;
        }
    }

    *[data-tip] {
        cursor: help;
    }

    .react-tiny-popover-container {
        z-index: ${Z_INDEX.REACT_TINY_POPOVER};
    }

    .ReactModal__Content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .ReactModal__Overlay {
        position: fixed;
        z-index: ${Z_INDEX.MODAL};
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(21, 24, 25, 0.6) !important;
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .select-none {
        user-select: none;
    }

    .mantine-Text-root, .mantine-Title-root, span {
        white-space: pre-line;
    }

    /* v7's .mantine-Text-root hard-codes font-weight: normal, which clobbers the
       weight a table cell/column cascades into its Text (v6 left it to inherit).
       Restore inheritance for value cells across both table variants: real td
       (StrawberryTable) and the grid role="cell" divs (base Table + MantineTable). */
    td .mantine-Text-root,
    [role='cell'] .mantine-Text-root {
        font-weight: inherit;
    }

    /* Slim, unobtrusive scrollbar for the MantineTable scroll area. */
    .mantine-table-scroll-area {
        scrollbar-width: thin;
        scrollbar-color: #bdc4c8 transparent;
    }

    .mantine-table-scroll-area::-webkit-scrollbar {
        width: 0.5rem;
        height: 0.5rem;
    }

    .mantine-table-scroll-area::-webkit-scrollbar-track {
        background: transparent;
    }

    .mantine-table-scroll-area::-webkit-scrollbar-thumb {
        background-color: #bdc4c8;
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: content-box;
    }

    .mantine-table-scroll-area::-webkit-scrollbar-thumb:hover {
        background-color: #a7b0b5;
    }
`;

export const GLOBAL_MEDIA_QUERIES = {
	xsmobile: mqRem(360),
	xssmobile: mqRem(375),
	ssmobile: mqRem(450),
	smobile: mqRem(600),
	mobile: mqRem(768),
	tablet: mqRem(992),
	ltablet: mqRem(1080),
	laptop: mqRem(1200),
	llaptop: mqRem(1366),
	desktop: mqRem(1600),
	ldesktop: mqRem(1920),
};

export const tableLineHeight = 1.43;
export const headerHeight = '4.5rem';
